import Email from '@react-spectrum/s2/icons/Email';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback, useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { BlobIframe } from '@/components/blob/BlobIframe';
import { BlobImage } from '@/components/blob/BlobImage';
import { useOpenChatPanel } from '@/hooks/useOpenChatPanel.ts';
import { humanizeType } from '@/spaces/chat/panels/panelLabel';
import { isScheduleObject } from '@/spaces/chat/panels/scheduleUtils';
import { cn } from '@/utils/cn';

import type { Artifact } from '../../../../bridge/types/artifact.ts';

import { digitalAdTitle } from './adTitle';
import { resolveReferenceDroppedNotice } from './referenceDroppedNotice';

export const ROW_THUMB_CLASS =
	'relative w-[100px] shrink-0 self-stretch min-h-[100px] overflow-hidden rounded-lg border border-gray-200 bg-white';

export const CalendarGlyph = ({ muted }: { muted?: boolean }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		viewBox="0 0 20 20"
		fill="currentColor"
		className={`size-5 shrink-0 ${muted ? 'text-gray-400' : 'text-gray-700'}`}
		aria-hidden="true"
	>
		<path
			fillRule="evenodd"
			d="M5.75 2a.75.75 0 01.75.75V4h7V2.75a.75.75 0 011.5 0V4h.25A2.75 2.75 0 0118 6.75v8.5A2.75 2.75 0 0115.25 18H4.75A2.75 2.75 0 012 15.25v-8.5A2.75 2.75 0 014.75 4H5V2.75A.75.75 0 015.75 2zM3.5 8v7.25c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25V8h-13z"
			clipRule="evenodd"
		/>
	</svg>
);

const emailIconStyles = iconStyle({ size: 'XL', color: 'gray' });

type EmailInstanceDoc = {
	id: string;
	html_link?: string | null;
};

export const EmailThumbnail = ({ artifact }: { artifact: Artifact }) => {
	const bridgewayApi = useBridgewayApi();
	const userId = useUserId();
	const cosmosId =
		artifact.type === 'email_html'
			? (artifact.content.instance?.cosmos_id ?? null)
			: null;
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!userId || !cosmosId) {
			setPreviewUrl(null);
			return;
		}
		let cancelled = false;
		bridgewayApi
			.get<EmailInstanceDoc>(`/api/user/${userId}/email/instance/${cosmosId}`)
			.then((instance) => {
				if (cancelled) return;
				setPreviewUrl(instance.html_link?.trim() || null);
			})
			.catch(() => {
				if (cancelled) return;
				setPreviewUrl(null);
			});
		return () => {
			cancelled = true;
		};
	}, [bridgewayApi, cosmosId, userId]);

	if (previewUrl) {
		return (
			<div className={ROW_THUMB_CLASS}>
				<BlobIframe
					src={previewUrl}
					sandbox="allow-same-origin"
					title="Email preview"
					tabIndex={-1}
					className="pointer-events-none absolute top-0 left-0 origin-top-left
						border-0"
					style={{ width: 600, height: 1800, transform: 'scale(0.1667)' }}
				/>
			</div>
		);
	}

	return (
		<div
			className={cn(ROW_THUMB_CLASS, 'flex items-center justify-center')}
			aria-hidden="true"
		>
			<Email styles={emailIconStyles} />
		</div>
	);
};

const MonitorThumbnail = () => (
	<div
		className={cn(
			ROW_THUMB_CLASS,
			'flex items-center justify-center bg-gray-100 text-gray-400',
		)}
		aria-hidden="true"
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			className="size-12"
		>
			<path d="M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 00-1.032-.211 50.89 50.89 0 00-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 002.433 3.984L7.28 21.53A.75.75 0 016 21v-4.03a48.527 48.527 0 01-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979z" />
		</svg>
	</div>
);

const GenericThumbnail = () => (
	<div
		className={cn(
			ROW_THUMB_CLASS,
			'flex items-center justify-center bg-gray-100 text-gray-400',
		)}
		aria-hidden="true"
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="currentColor"
			className="size-12"
		>
			<path
				fillRule="evenodd"
				d="M4 4.5A2.5 2.5 0 016.5 2h11A2.5 2.5 0 0120 4.5v15a2.5 2.5 0 01-2.5 2.5h-11A2.5 2.5 0 014 19.5v-15zM8 7a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h4a1 1 0 110 2H9a1 1 0 01-1-1z"
				clipRule="evenodd"
			/>
		</svg>
	</div>
);

const ArtifactThumbnail = ({ artifact }: { artifact: Artifact }) => {
	if (artifact.type === 'instagram_post') {
		const src = artifact.content.image_urls?.[0];
		if (src) {
			return (
				<BlobImage
					src={src}
					alt=""
					className={cn(ROW_THUMB_CLASS, 'object-cover')}
				/>
			);
		}
		return (
			<div className={cn(ROW_THUMB_CLASS, 'bg-gray-100')} aria-hidden="true" />
		);
	}
	if (artifact.type === 'email_html' || artifact.type === 'email_plaintext') {
		return <EmailThumbnail artifact={artifact} />;
	}
	if (
		artifact.type === 'ig_comment_watcher_class' ||
		artifact.type === 'subreddit_monitor'
	) {
		return <MonitorThumbnail />;
	}
	return <GenericThumbnail />;
};

export type StatusTone =
	| 'posted'
	| 'scheduled'
	| 'failed'
	| 'draft'
	| 'neutral';

export type ArtifactStatusLine = {
	label: string;
	tone: StatusTone;
};

const deriveArtifactStatusLine = (artifact: Artifact): ArtifactStatusLine => {
	switch (artifact.status) {
		case 'published':
			if (artifact.type === 'instagram_post') {
				return { label: 'Posted', tone: 'posted' };
			}
			if (
				artifact.type === 'email_html' ||
				artifact.type === 'email_plaintext'
			) {
				return { label: 'Sent', tone: 'posted' };
			}
			return { label: 'Done', tone: 'posted' };
		case 'scheduled':
			return { label: 'Scheduled', tone: 'scheduled' };
		case 'processing':
			return { label: 'Processing', tone: 'scheduled' };
		case 'failed':
			return { label: 'Failed', tone: 'failed' };
		case 'cancelled':
			return { label: 'Cancelled', tone: 'neutral' };
		case 'proposed':
			return { label: 'Proposed', tone: 'draft' };
		// An agent submitting a single item for approval writes "pending"; to a
		// customer that is the same state as a campaign's draft.
		case 'draft':
		case 'pending':
			return { label: 'Draft', tone: 'draft' };
		case 'active':
			return { label: 'Active', tone: 'posted' };
		case 'paused':
			return { label: 'Paused', tone: 'neutral' };
		case 'applied':
			return { label: 'Applied', tone: 'posted' };
		case 'rejected':
			return { label: 'Rejected', tone: 'neutral' };
		default:
			// Every status in the union is handled above, so this is unreachable
			// today — kept as a safety net for a status a stored document carries
			// that the type no longer names.
			return {
				label: humanizeType((artifact as Artifact).type),
				tone: 'neutral',
			};
	}
};

const artifactTitle = (artifact: Artifact): string => {
	switch (artifact.type) {
		case 'instagram_post':
			return (
				artifact.content.caption?.split('\n')[0]?.slice(0, 80) ||
				'Instagram post'
			);
		case 'instagram_dm':
			return artifact.content.recipient_handle
				? `DM to @${artifact.content.recipient_handle}`
				: 'Instagram DM';
		case 'instagram_comment_reply':
			return `Reply to @${artifact.content.commenter_username}`;
		case 'email_html':
			return artifact.content.subject || artifact.content.name || 'Email';
		case 'email_plaintext':
			return artifact.content.subject || 'Email';
		case 'ig_comment_watcher_class':
			return artifact.content.name || 'Comment watcher';
		case 'subreddit_monitor':
			return artifact.content.name || 'Subreddit monitor';
		case 'digital_ad':
			return digitalAdTitle(artifact.content, 'Digital ad');
		case 'deferred_content':
			return artifact.content.instructions?.slice(0, 80) || 'Deferred content';
		case 'proposed_edit':
			return 'Proposed edit';
		default: {
			// Every type in the union is handled above, so this is unreachable
			// today — kept as a safety net for a type a stored document carries
			// that the type no longer names.
			const unknownArtifact = artifact as Artifact;
			return unknownArtifact.user_summary || humanizeType(unknownArtifact.type);
		}
	}
};

// ADO-1738. `user_summary` is LLM-authored and written before generation runs,
// so a substituted post's summary can still promise a photo the delivered
// image does not contain. This is the defensive half: the backend now
// corrects `user_summary` at substitution time, but posts substituted before
// that landed still carry the stale summary in storage, so the row corrects
// it on read too.
const rowSummary = (artifact: Artifact): string | null => {
	if (artifact.type === 'instagram_post') {
		const notice = resolveReferenceDroppedNotice(artifact.content, false);
		if (notice) return notice;
	}
	return artifact.user_summary || null;
};

const formatScheduleDate = (artifact: Artifact): string | null => {
	if (artifact.type !== 'instagram_post') return null;
	const schedule = artifact.schedule;
	if (!schedule) return null;
	// A legacy row stores the schedule as a bare string, and `in` raises on one.
	if (!isScheduleObject(schedule)) return null;
	if (schedule.kind === 'absolute') {
		try {
			const iso = `${schedule.on}T${schedule.at}`;
			return new Date(iso).toLocaleString(undefined, {
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
			});
		} catch {
			return `${schedule.on} · ${schedule.at}`;
		}
	}
	// A post that publishes on activation has no date to promise yet.
	if ('fire_at' in schedule && schedule.fire_at) {
		try {
			return new Date(schedule.fire_at).toLocaleString(undefined, {
				month: 'short',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
			});
		} catch {
			return schedule.fire_at;
		}
	}
	return null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const emailSubject = (artifact: Artifact): string | null => {
	if (artifact.type === 'email_html' || artifact.type === 'email_plaintext') {
		return artifact.content.subject || null;
	}
	return null;
};

interface ArtifactRowProps {
	artifact: Artifact;
}

export const ArtifactRow = ({ artifact }: ArtifactRowProps) => {
	const openChatPanel = useOpenChatPanel();

	const handleClick = useCallback(() => {
		openChatPanel({
			panel_type: 'artifact',
			artifact_type: artifact.type,
			item_id: artifact.id,
		});
	}, [openChatPanel, artifact.id, artifact.type]);

	const status = deriveArtifactStatusLine(artifact);
	const title = artifactTitle(artifact);
	const summary = rowSummary(artifact);
	const subject = emailSubject(artifact);
	const scheduleDate = formatScheduleDate(artifact);
	const statusText = scheduleDate
		? `${status.label} · ${scheduleDate}`
		: status.label;

	return (
		<button
			type="button"
			onClick={handleClick}
			className="flex w-full items-stretch gap-4 py-4 text-left
				transition-colors hover:bg-gray-50"
		>
			<ArtifactThumbnail artifact={artifact} />
			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<div className="flex flex-col">
					<p className="line-clamp-1 text-lg font-bold text-gray-900">
						{title}
					</p>
					{subject && (
						<p className="line-clamp-1 text-base text-gray-500">{subject}</p>
					)}
					{!subject && summary && (
						<p className="line-clamp-2 text-base text-gray-500">{summary}</p>
					)}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<CalendarGlyph muted />
					<span className="text-base text-gray-700">{statusText}</span>
				</div>
			</div>
		</button>
	);
};
